import { yupResolver } from "@hookform/resolvers/yup";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { cn } from "@omni/utils/styles";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";

import { useAddressValidationContext } from "@/components/Address/context/AddressValidation/addressValidation.context";
import { CustomerAddressFormBlock } from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.container";
import {
    CustomerDefaultAddressFormValues,
    CustomerDefaultAddressValidationSchema
} from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.schema";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Messages } from "@/components/General/Messages/messages.container";
import { Modal } from "@/components/General/Modal/modal";
import * as AddressSelectors from "@/redux/modules/api/data/address/address.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getEmptyAddress } from "@/utils/address/address";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

type AddNewAddressModalProps = {
    isOpen: boolean;
    className?: string;
    toggleHandler: (isOpen: boolean) => void;
    createAddress: (
        addressing: string | null | undefined,
        firstName: string,
        lastName: string,
        country: string,
        city: string,
        street: string,
        box: string | null | undefined,
        number: string,
        zipCode: string,
        validationStatus: AddressValidationStatus
    ) => Promise<void>;
};

const FORM_ID = "addNewAddressModal";

const CreateAddressModal = ({
    isOpen,
    className,
    createAddress,
    toggleHandler
}: AddNewAddressModalProps) => {
    const { t } = useGranite();
    const { validate, isValidating } = useAddressValidationContext();
    const isLoadingAddress = useSelector(AddressSelectors.isLoading);

    const toggleModal = () => toggleHandler(!isOpen);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: getEmptyAddress(AddressType.Delivery),
        resolver: yupResolver(CustomerDefaultAddressValidationSchema)
    });

    const onSubmit = async (address: CustomerDefaultAddressFormValues) => {
        clearMessageById(BPOSTInvalidAddressMessageId);

        const validationResult = await validate({
            id: undefined,
            firstName: address.firstName,
            lastName: address.lastName,
            street: address.street,
            number: address.number,
            zipCode: address.zipCode,
            city: address.city,
            country: address.country,
            box: address.box,
            addressing: address.addressing,
            type: null,
            isBpostField: !!address.selected,
            currentStatus: AddressValidationStatus.Unknown
        });

        if (validationResult === AddressValidationStatus.Invalid) {
            broadcastMessage({
                id: BPOSTInvalidAddressMessageId,
                text: t("bpost.validation.address.invalid"),
                status: "warning",
                dismissible: true
            });
            return;
        }

        if (validationResult === AddressValidationStatus.Closed) {
            return;
        }

        if (validationResult) {
            await createAddress(
                address.addressing,
                address.firstName,
                address.lastName,
                address.country,
                address.city,
                address.street,
                address.box,
                address.number,
                address.zipCode,
                validationResult
            );
        }

        toggleModal();

        // Delay reset until after React Hook Form finishes the current submit cycle,
        // otherwise formState (isSubmitted, submitCount, validation state) may persist
        setTimeout(() => methods.reset());
    };

    return (
        <Modal
            title={t("account.create.address.title")}
            formId={FORM_ID}
            size={"lg"}
            open={isOpen}
            setOpen={toggleModal}
            isLoading={isValidating || isLoadingAddress}
            confirmButtonText={t("general.address.modal.button.label")}
            className={cn(className)}
        >
            <Messages messageid={BPOSTInvalidAddressMessageId} />
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onSubmit)}
                        >
                            <CustomerAddressFormBlock<
                                CustomerDefaultAddressFormValues,
                                AddressType.Default
                            >
                                addressType={AddressType.Default}
                                addressStatus={AddressValidationStatus.Unknown}
                                skipValidation={true}
                            />
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
        </Modal>
    );
};

export { CreateAddressModal };
